import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, ShieldOff, FolderOpen } from "lucide-react";
import { fetchCockpitUserDetail, type CockpitUserDetail as UserDetailType } from "../../lib/admin";
import { supabase } from "../../lib/supabase";
import { useDossierStore } from "../../store/dossierStore";

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "—";
  }
}

export default function CockpitUserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const setActiveDossier = useDossierStore((s) => s.setActiveDossier);
  const [data, setData] = useState<UserDetailType | null | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    fetchCockpitUserDetail(id)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch(() => {
        if (alive) setErr("Chargement impossible.");
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const openDossier = async (dossierId: string) => {
    const { data: row, error } = await supabase
      .from("dossiers")
      .select("id, nom, cabinet_id")
      .eq("id", dossierId)
      .maybeSingle();
    if (error || !row) return;
    setActiveDossier({
      id: row.id,
      nom: row.nom,
      cabinet_id: row.cabinet_id ?? undefined,
    });
    navigate("/dashboard");
  };

  if (err) {
    return <p className="text-sm text-red-400">{err}</p>;
  }
  if (data === undefined) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }
  if (!data) {
    return <p className="text-slate-400">Utilisateur introuvable.</p>;
  }

  const email = data.user.email;
  const pr = data.profile;

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <Link
        to="/cockpit/users"
        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-orange-400"
      >
        <ArrowLeft size={16} /> Retour à la liste
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">{email}</h2>
          <p className="mt-1 text-sm text-slate-400">ID : {data.user.user_id}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => window.open(`mailto:${encodeURIComponent(email)}`, "_blank")}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 hover:border-orange-500/50"
          >
            <Mail size={16} /> Envoyer un e-mail
          </button>
          <button
            type="button"
            onClick={() => {
              /* Blocage compte : réservé à une future intégration Auth Admin */
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-500"
            disabled
            title="Bientôt disponible (API admin)"
          >
            <ShieldOff size={16} /> Bloquer
          </button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-lg border border-slate-700/80 bg-slate-800/40 p-5">
          <h3 className="text-sm font-semibold text-white">Profil</h3>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Prénom / Nom</dt>
              <dd className="text-slate-200">
                {[pr?.prenom, pr?.nom].filter(Boolean).join(" ") || "—"}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Téléphone</dt>
              <dd className="text-slate-200">{(pr?.telephone as string) || "—"}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Pays</dt>
              <dd className="text-slate-200">{(pr?.pays as string) || "—"}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Entreprise</dt>
              <dd className="text-slate-200">{(pr?.entreprise as string) || "—"}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-slate-700/80 bg-slate-800/40 p-5">
          <h3 className="text-sm font-semibold text-white">Connexion</h3>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Compte créé</dt>
              <dd className="text-slate-200">{fmtDate(data.user.created_at)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Dernière connexion</dt>
              <dd className="text-slate-200">{fmtDate(data.user.last_sign_in_at)}</dd>
            </div>
          </dl>
          <p className="mt-4 text-xs text-slate-500">
            Historique détaillé des sessions : à brancher sur les journaux Auth si besoin.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-slate-700/80 bg-slate-800/40 p-5">
        <h3 className="text-sm font-semibold text-white">Dossiers</h3>
        {data.dossiers.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">Aucun dossier lié.</p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-700/80">
            {data.dossiers.map((d) => (
              <li key={`${d.dossier_id}-${d.lien}`} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div>
                  <Link
                    to={`/cockpit/dossiers/${d.dossier_id}`}
                    className="font-medium text-orange-400 hover:text-orange-300"
                  >
                    {d.nom_dossier}
                  </Link>
                  <p className="text-xs text-slate-500">
                    {d.pays || "—"} · {d.regime_fiscal || "—"} · {d.lien}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openDossier(d.dossier_id)}
                  className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-500"
                >
                  <FolderOpen size={14} /> Accéder au dossier
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
